import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Image } from 'remix-image';
import { Attributes, AttributesStep, ReadOnlyAttributes } from '~/components/ProductDetails/Attributes';
import { LoadingWrapper } from '~/components/ProductDetails/MediaSection';
import { MuiRemixLink } from '~/components/shared/links';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Variant } from '~/services/api-utils.server';
import type { Product } from '~/services/api.server';
import { getFormattedPrice } from '~/utils/common';

export function PackItem({
  product,
  selectedAttributes = getPlainAttributes(product.default_part.variant),
  unitsPerPack,
  price,
  onAttributeChange,
  badge,
  image = <PackItemImage src={product.image} alt={product.name} />,
}: {
  product: Product;
  selectedAttributes?: { [k: string]: string };
  unitsPerPack: number;
  price: number;
  onAttributeChange: (product: number, attribute: string, value: string, newAttribues?: Record<string, string>) => void;
  image?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <PackItemInfo
      product={product}
      selectedAttributes={selectedAttributes}
      unitsPerPack={unitsPerPack}
      price={price}
      image={image}
      badge={badge}
    >
      <Attributes
        availableAttributes={product.available_attributes}
        selection={selectedAttributes}
        onAttributeChange={(attribute, value, newAttribues) =>
          onAttributeChange(product.id, attribute, value, newAttribues)
        }
        unavailableVariants={product.unavailable_variants}
        unavailableInventoryParts={product.unavailable_inventory_parts}
      />
    </PackItemInfo>
  );
}

export function PackItemAttributesSteps({
  product,
  selectedAttributes = getPlainAttributes(product.default_part.variant),
  unitsPerPack,
  price,
  onAttributeChange,
  badge,
  image = <PackItemImage src={product.image} alt={product.name} />,
  colorStep,
}: {
  product: Product;
  selectedAttributes?: { [k: string]: string };
  unitsPerPack: number;
  price: number;
  onAttributeChange: (product: number, attribute: string, value: string, newAttribues?: Record<string, string>) => void;
  image?: React.ReactNode;
  badge?: React.ReactNode;
  colorStep: boolean;
}) {
  return (
    <PackItemInfo
      product={product}
      selectedAttributes={selectedAttributes}
      unitsPerPack={unitsPerPack}
      price={price}
      image={image}
      badge={badge}
    >
      <AttributesStep
        availableAttributes={product.available_attributes}
        selection={selectedAttributes}
        onAttributeChange={(attribute, value, newAttribues) =>
          onAttributeChange(product.id, attribute, value, newAttribues)
        }
        unavailableVariants={product.unavailable_variants}
        colorStep={colorStep}
        unavailableInventoryParts={product.unavailable_inventory_parts}
      />
    </PackItemInfo>
  );
}

function PackItemInfo({
  product,
  unitsPerPack,
  price,
  badge,
  image = <PackItemImage src={product.image} alt={product.name} />,
  children,
}: {
  product: Product;
  selectedAttributes?: { [k: string]: string };
  unitsPerPack: number;
  price: number;
  image?: React.ReactNode;
  badge?: React.ReactNode;
  children: JSX.Element;
}) {
  return (
    <Stack direction="row" bgcolor="#FFF" width="100%" borderRadius={2.5} p={{ xs: 1, sm: 2 }} gap={2}>
      <Stack alignItems="center" gap={1}>
        {image}
        {badge}
      </Stack>
      <Stack gap={1} width="100%" >
        <Typography variant="subtitle1">
          <MuiRemixLink href={`/shop/${product.slug}`} underline="hover" color="#212121">
            {product.name}
          </MuiRemixLink>
          &nbsp;
          <Tooltip title="Qty per pack" arrow placement="top">
            <Typography variant="body2" color="text.secondary" component="span">
              ({unitsPerPack})
            </Typography>
          </Tooltip>
        </Typography>

        <Typography variant="subtitle2" color="#212121">Item price: {getFormattedPrice(price / 100)}</Typography>

        {children}
      </Stack>
    </Stack>
  );
}

export function PackItemReadOnlyAttributes({
  product,
  selectedAttributes = getPlainAttributes(product.default_part.variant),
  unitsPerPack,
  price,
  badge,
  image = <PackItemImage src={product.image} alt={product.name} />,
}: {
  product: Product;
  selectedAttributes?: { [k: string]: string };
  unitsPerPack: number;
  price: number;
  image?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <PackItemInfo
      product={product}
      selectedAttributes={selectedAttributes}
      unitsPerPack={unitsPerPack}
      price={price}
      image={image}
      badge={badge}
    >
      <ReadOnlyAttributes
        availableAttributes={product.available_attributes}
        selection={selectedAttributes}
        unavailableVariants={product.unavailable_variants}
        unavailableInventoryParts={product.unavailable_inventory_parts}
      />
    </PackItemInfo>
  );
}

export function PackItemImage({
  src,
  alt,
  isLoading = false,
}: {
  alt: string;
  src?: string | null;
  isLoading?: boolean;
}) {
  return (
    <Stack justifyContent="center" alignItems="center" sx={{ position: 'relative' }}>
      <LoadingWrapper isLoading={isLoading}>
        {src ? (
          <Image
            src={src}
            alt={alt}
            responsive={[{ size: { width: 64, height: 64 } }]}
            options={{ fit: 'inside' }}
            dprVariants={[1, 3]}
            style={{ width: 64, height: 64 }}
          />
        ) : (
          <img src={NoImageAvailable} alt="not available" style={{ width: 64, height: 64 }} />
        )}
      </LoadingWrapper>
    </Stack>
  );
}

export function getPlainAttributes(variant: Variant) {
  return Object.fromEntries(Object.entries(variant).map(([key, value]) => [key, value.name]));
}
